import { getDateQuarter, getDateString, getPreviousDate } from "common/date-utils";
import { formatPointLabel, formatPointLabelForAxis } from "components/widgets/chart/base/utils";
import { language } from "language/default";
import { ChartSelectValueFormatKind, DataGranularity } from "store/current-dashboard/types";
import { getTextWidth } from "@blk/aladdin-web-components";
export const yAxisFormatter = (context, currency, format, unit, yAxisLimits) => {
    if (typeof context.value === "number") {
        let formattedValue = formatPointLabelForAxis(context.value, format, unit, { currency });
        if (yAxisLimits) {
            if (context.value === yAxisLimits.min && yAxisLimits.minLabelPrefix) {
                formattedValue = yAxisLimits.minLabelPrefix + formattedValue;
            }
            if (context.value === yAxisLimits.max && yAxisLimits.maxLabelPrefix) {
                formattedValue = yAxisLimits.maxLabelPrefix + formattedValue;
            }
        }
        return formattedValue;
    }
    return context.axis.defaultLabelFormatter.call(context);
};
export const formatToQuarterForXAxis = (context, granularity, dualChartCompatibility = false) => {
    if (typeof context.value === "number") {
        const firstDayOfQuarter = new Date(context.value), lastDayOfPreviousQuarter = getPreviousDate(firstDayOfQuarter), quarter = getDateQuarter(lastDayOfPreviousQuarter), 
        // With DualAxisChart context.value is at the start of the year, with TimeSeriesChart it is at the reference date
        year = (dualChartCompatibility ? firstDayOfQuarter : lastDayOfPreviousQuarter).getUTCFullYear();
        return granularity === DataGranularity.quarterly ? `Q${quarter}<br />${lastDayOfPreviousQuarter.getFullYear()}` : year.toString();
    }
    return context.axis.defaultLabelFormatter.call(context);
};
const monthsInYear = 12;
const minPixelTickInterval = 50;
const monthDiff = (dateFrom, dateTo) => dateTo.getMonth() - dateFrom.getMonth() + (monthsInYear * (dateTo.getFullYear() - dateFrom.getFullYear()));
const toTickDate = (date) => new Date(date.getFullYear(), getDateQuarter(date) * 3, 1);
const getTickRate = (tickPositions, pixelTickInterval) => {
    if (tickPositions.length > 2) {
        return monthDiff(new Date(tickPositions[0]), pixelTickInterval < minPixelTickInterval ? new Date(tickPositions[3]) : new Date(tickPositions[1]));
    }
    return 0;
};
const calculateFirstTickDate = (minAxisDate, tickRate) => {
    let firstTickDate = minAxisDate.getMonth() === 3 ? new Date(minAxisDate.getFullYear(), 3, 1) : new Date(minAxisDate.getFullYear() + 1, 3, 1);
    let newCandidateDate = new Date(firstTickDate);
    while (newCandidateDate >= minAxisDate) {
        firstTickDate = new Date(newCandidateDate);
        newCandidateDate = new Date(newCandidateDate.setMonth(newCandidateDate.getMonth() - tickRate));
    }
    return firstTickDate;
};
const calculatePixelTickInterval = (axis) => {
    if (axis?.tickPositions && axis?.chart?.chartWidth) {
        return axis.chart.chartWidth / axis?.tickPositions.length;
    }
    return minPixelTickInterval;
};
const getQuarterTicksPositions = (axis) => {
    const ticks = [];
    if (axis?.tickPositions) {
        const [tickRate, minAxisDate, maxAxisDate] = [
            getTickRate(axis.tickPositions, calculatePixelTickInterval(axis)),
            new Date(axis.min),
            new Date(axis.max)
        ];
        if (tickRate > 0) {
            let currentDate = calculateFirstTickDate(minAxisDate, tickRate);
            ticks.pushIfNotExist(toTickDate(minAxisDate).getTime());
            while (currentDate <= maxAxisDate) {
                currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + tickRate));
                ticks.pushIfNotExist(currentDate.getTime());
            }
        }
    }
    return ticks;
};
const allLabelsFit = (datapointData, axis) => {
    const labelWidth = getTextWidth("2025", datapointData?.dataLabels?.xAxisLabelFontSize);
    const { chartWidth } = axis.chart || {};
    const maxLabels = Math.max(...(datapointData?.data || []).map((item) => item.data.length));
    const totalRequiredWidth = maxLabels * labelWidth;
    return totalRequiredWidth <= chartWidth;
};
export const getTicksPositions = (axis, granularity, customTickPositions, datapointData) => {
    if (customTickPositions?.length) {
        return customTickPositions;
    }
    if (granularity === DataGranularity.quarterly || (granularity === DataGranularity.yearly && !allLabelsFit(datapointData, axis))) {
        return getQuarterTicksPositions(axis);
    }
    return axis.tickPositions;
};
export const getLabelValuePairs = (context, chartSelectValues, selectedCurrency, customMapping, format) => {
    const labelValuePairs = [], { index } = context.point.series, values = chartSelectValues.find((val) => val.order === index) ?? chartSelectValues[0];
    let valueFormat = isNaN(values?.valueFormat) ? ChartSelectValueFormatKind[values?.valueFormat] : values?.valueFormat, valueUnit = values?.valueUnit;
    if (customMapping === "VariationGroupBy") {
        valueFormat = chartSelectValues[0].valueFormat;
        valueUnit = chartSelectValues[0].valueUnit;
    }
    labelValuePairs.push([language.en.chart.timeBasedSeries.tooltip.referenceDate, getDateString(new Date(context.point.options?.custom?.referenceDate ?? context.point.x))]);
    const percentFormatOptions = format === ChartSelectValueFormatKind.percent ? { minimumFractionDigits: 2 } : {};
    if (context.point.low && context.point.high) {
        const high = formatPointLabel(context.point.high, valueFormat, valueUnit, { ...percentFormatOptions, currency: selectedCurrency }), low = formatPointLabel(context.point.low, valueFormat, valueUnit, { ...percentFormatOptions, currency: selectedCurrency });
        labelValuePairs.push([
            context.point.series.name,
            `${low} - ${high}`
        ]);
    }
    else {
        labelValuePairs.push([
            context.point.series.name,
            formatPointLabel(context.point.y, (valueFormat || format), valueUnit, { ...percentFormatOptions, currency: selectedCurrency })
        ]);
    }
    return labelValuePairs;
};
